import Company from '@/models/Company'
import SystemDashboard from '@/models/SystemDashboard'
import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import SelectOption from '@/models/interface/SelectOption'
import moment from 'moment'
import RealTimeTodayWidget from '../widgets/channels/RealTimeTodayWidget'
import MLBDelivered from '../widgets/delivery/MLBDelivered'
import ActivityThisWeekWidget from '../widgets/finance/ActivityThisWeekWidget'
import PublisherShareWidget from '../widgets/finance/PublisherShareWidget'
import DailyImpressions from '../widgets/delivery/DailyImpressions'
import SaleProfitTableWidget from '../widgets/finance/SalesChannelProfitWidget'
import YearMonthQuarterColumnToggle from '../filters/YearMonthQuarterColumnToggle'
import GoalPickerWidget from '../widgets/others/GoalPickerWidget'

// Label for a goal in the picker — name if present, else date range.
// Re-exported for the unit test that locks in the precedence behavior.
export const goalPickerLabel = (goal: any): string => {
  if (goal?.name) {
    return goal.name
  }
  const start = goal?.start_at ? moment(goal.start_at).format('MMM D, YYYY') : ''
  const end = goal?.end_at ? moment(goal.end_at).format('MMM D, YYYY') : ''
  return start && end ? `${start} – ${end}` : 'Untitled goal'
}

// Read the live goal_id from the URL filters. DashboardView updates the URL
// synchronously inside updateProp before each widget's deferred (~100ms)
// dataSource fires, so closures can rely on this returning the current value.
const readGoalIdFromUrl = (): string => {
  if (typeof window === 'undefined') {
    return ''
  }
  const params = new URLSearchParams(window.location.search)
  const filters = params.getAll('filters[]').concat(params.getAll('filters'))
  for (const f of filters) {
    const [k, v] = f.split(':')
    if (k === 'goal_id' && v) {
      return v
    }
  }
  return ''
}

// Mirrors backend Company::resolveDeliveryGoal precedence:
// url goal_id → is_default → active-by-date (today ∈ [start,end]) → first.
export const resolveInitialGoalId = (goals: any[], urlGoalId: string | null): string => {
  if (!goals?.length) {
    return ''
  }
  if (urlGoalId && goals.some(g => g.id === urlGoalId)) {
    return urlGoalId
  }
  const flagged = goals.find(g => g.is_default)
  if (flagged) {
    return flagged.id
  }
  const today = moment()
  const active = goals.find(
    g =>
      today.isSameOrAfter(moment(g.start_at), 'day')
      && today.isSameOrBefore(moment(g.end_at), 'day'),
  )
  if (active) {
    return active.id
  }
  return goals[0].id
}

const cpm_total = (primary_key: string, secondary_key: string, data: any[]) => {
  const primary = data.reduce((acc, item) => {
    const value = item[primary_key] || 0
    return acc + value
  }, 0)
  const secondary = data.reduce((acc, item) => {
    const value = item[secondary_key] || 0
    return acc + value
  }, 0)

  return primary && secondary ? (primary / secondary) * 1000 : 0
}

export default class MLBModule extends SystemDashboard {
  public title: string = 'MLB'

  public key = 'mlb_dashboard'

  public logo: string = '/images/mlb.logo.png'

  public filters: any = {
    company_id: 'f37c9dd6-4776-4122-8a37-37d67f4ff47a',
    goal_id: '',
  }

  public show_logo_on_export: boolean = false

  public show_goal_picker: boolean = false

  constructor(args: any = null) {
    super()

    if (args && args.logo) {
      this.logo = args.logo
    }

    if (args && args.key) {
      this.key = args.key
    }

    if (args && args.title) {
      this.title = args.title
    }

    if (args && typeof args.show_logo_on_export !== 'undefined') {
      this.show_logo_on_export = args.show_logo_on_export
    }

    if (args && args.filters) {
      this.filters = { ...this.filters, ...args.filters }
    }

    if (args && args.show_goal_picker === true) {
      this.show_goal_picker = true
      // Prepend the picker widget so Vue observes it in the initial tree.
      this.widgets = [new GoalPickerWidget({ companyId: this.filters.company_id }), ...this.widgets]
    }

    if (args && args.hide_goal_tracker === true) {
      this.widgets = this.widgets
        .filter(widget => widget.key !== 'mlb-gauge')
        .map(widget => {
          if (widget.key === 'mlb-delivery-status') {
            widget.size = {
              lg: 12,
              md: 12,
            }
          }
          return widget
        })
    }
  }

  /**
   * Seed filters.goal_id from the URL so the initial widget renders fire with
   * the right goal. The actual picker UI lives in GoalPickerWidget; it owns
   * the fetch and option list. Goal-aware widget closures read goal_id from
   * the URL at call time (DashboardView updates the URL synchronously when
   * the picker emits, before deferred dataSource fires).
   */
  public setup(): void {
    if (this.show_goal_picker) {
      const urlGoalId = readGoalIdFromUrl()
      if (urlGoalId) {
        this.filters.goal_id = urlGoalId
      }
    }
  }

  public get customRight() {
    return `<div class="mb-5 ml-3 text-right">
      <img src="${this.logo}" alt="Logo" class="invoiceLogo page-ready" height="80px" />
      <img
        style="border-left: 0.1rem solid #97a3ae"
        src="/images/castiron_media.logo.svg"
        alt="Logo"
        class="invoiceLogo pl-3 ml-3"
        height="80px"
      />
    </div>`
  }

  public widgets: SystemDashboardWidget[] = [
    new RealTimeTodayWidget(),

    new SaleProfitTableWidget({
      title: 'Property Breakdown',
      conditionalFields: [],
      firstLabel: 'Property',
      internalQueryPush: null,
      _show: (module: SystemDashboardWidget) =>
        module.filters.company_id === 'a13cd34f-c7bc-4f39-937e-3197aea1a700',
      filters: { goal_id: '' },
      dataSourceRequest: (payload: any) =>
        Company.activeGoalMetrics({
          id: this.filters.company_id,
          goal_id: payload?.filters?.goal_id || readGoalIdFromUrl() || this.filters.goal_id,
          property_breakdown: true,
        }),
      rightCol: [
        new YearMonthQuarterColumnToggle({
          options: [
            new SelectOption('Impressions', 'show_impressions'),
            new SelectOption('CPM', 'show_cpm'),
            new SelectOption('Revenue', 'show_revenue'),
          ],
        }),
      ],
      internalProps: {
        show_yesterday: true,
        show_mtd: true,
        show_quarter: true,
        show_year: true,
        show_impressions: true,
        show_revenue: true,
        show_cpm: true,
        show_profit: false,
        single_control: true,
      },
      fields: [
        {
          colspan: 1,
          key: 'label',
          label: '',
          sortable: false,
          filter: false,
          show: true,
          thClass: 'p-0 align-middle text-center ',
          tdClass: 'align-middle text-center p-0 text-capitalize',
          thStyle: 'min-width:200px!important; width:400px!important',
        },
        {
          colspan: 3,
          key: 'yesterday',
          label: 'Yesterday',
          sortable: false,
          filter: false,
          show: true,
          thClass: 'p-0  align-middle text-center ',
          tdClass: 'align-middle text-center p-0 text-capitalize',
          thStyle: 'min-width:200px!important; width:200px!important',
        },
        {
          colspan: 3,
          key: 'mtd',
          label: 'MTD',
          sortable: false,
          filter: false,
          show: true,
          thClass: 'p-0  align-middle text-center ',
          tdClass: 'align-middle text-center p-0 text-capitalize',
          thStyle: 'min-width:200px!important; width:200px!important',
        },
        {
          colspan: 3,
          key: 'year',
          label: 'Season',
          sortable: false,
          filter: false,
          show: true,
          thClass: 'p-0  align-middle text-center',
          tdClass: 'align-middle text-center p-0 text-capitalize',
          thStyle: 'min-width:200px!important; width:200px!important',
        },
      ],
      subFields: [
        {
          key: 'label',
          label: '',
          show: true,
          footer: { label: 'Total' },
          thClass: 'align-middle text-left',
          cellClass: 'align-middle',
        },
        {
          key: 'yesterday_impressions',
          label: 'Impressions',
          show: true,
          type: 'numeral',
          tdClass: 'align-middle text-center',
          thClass: 'align-middle text-center',
        },
        {
          key: 'yesterday_cpm',
          label: 'CPM',
          show: false,
          type: 'currency',
          tdClass: 'align-middle text-center',
          thClass: 'align-middle text-center',
          customTotal: (data: any) => cpm_total('revenue', 'impressions', data),
        },
        {
          key: 'yesterday_revenue',
          label: 'Revenue',
          show: true,
          type: 'currency',
          tdClass: 'align-middle text-center',
          thClass: 'align-middle text-center',
        },
        {
          key: 'mtd_impressions',
          label: 'Impressions',
          show: true,
          type: 'numeral',
          tdClass: 'align-middle text-center',
          thClass: 'align-middle text-center',
        },
        {
          key: 'mtd_cpm',
          label: 'CPM',
          show: false,
          type: 'currency',
          tdClass: 'align-middle text-center',
          thClass: 'align-middle text-center',
          customTotal: (data: any) => cpm_total('revenue', 'impressions', data),
        },
        {
          key: 'mtd_revenue',
          label: 'Revenue',
          show: true,
          type: 'currency',
          tdClass: 'align-middle text-center',
          thClass: 'align-middle text-center',
        },
        {
          key: 'year_impressions',
          label: 'Impressions',
          show: true,
          type: 'numeral',
          tdClass: 'align-middle text-center',
          thClass: 'align-middle text-center',
        },
        {
          key: 'year_cpm',
          label: 'CPM',
          show: false,
          type: 'currency',
          tdClass: 'align-middle text-center',
          thClass: 'align-middle text-center',
          customTotal: (data: any) => cpm_total('revenue', 'impressions', data),
        },
        {
          key: 'year_revenue',
          label: 'Revenue',
          show: true,
          type: 'currency',
          tdClass: 'align-middle text-center',
          thClass: 'align-middle text-center',
        },
      ],
    }),

    new ActivityThisWeekWidget({
      title: 'Delivery Status Report',
      key: 'mlb-delivery-status',
      fields: [
        {
          key: 'period',
          label: 'Period',
          class: 'text-center align-middle',
          type: 'uppercase',
          show: true,
          thClass: 'align-middle text-center',
          sortable: false,
          total: 'none',
        },
        {
          key: 'impressions',
          label: 'Impressions',
          class: 'text-center align-middle',
          show: true,
          thClass: 'align-middle text-center',
          type: 'abbreviate',
          sortable: false,
        },
        {
          key: 'cpm',
          label: 'CPM',
          class: 'text-center align-middle',
          show: true,
          thClass: 'align-middle text-center',
          type: 'currency',
          sortable: false,
        },
        {
          key: 'earned_revenue',
          label: 'Earned Revenue',
          class: 'text-center align-middle',
          show: true,
          thClass: 'align-middle text-center',
          type: 'currency_abbreviate',
          sortable: false,
        },
      ],
      size: {
        lg: 8,
        md: 12,
      },
      noPagination: true,
      filters: { goal_id: '' },
      dataSourceRequest: (payload: any) =>
        Company.activeGoalMetrics({
          id: this.filters.company_id,
          goal_id: payload?.filters?.goal_id || readGoalIdFromUrl() || this.filters.goal_id,
        }),
    }),
    new MLBDelivered({
      title: 'Delivered vs Goal',
      key: 'mlb-gauge',
      dimensions: [],
      metrics: ['delivered', 'goal'],
      filters: { goal_id: '' },
      size: {
        lg: 4,
        md: 12,
      },
    }),

    new DailyImpressions({
      dataSourceRequest: (payload: any) =>
        Company.activeGoal30Days({
          id: this.filters.company_id,
          goal_id: payload?.filters?.goal_id || readGoalIdFromUrl() || this.filters.goal_id,
        }),
    }),
    new DailyImpressions({
      title: 'Monthly Impressions',
      key: 'mlb-monthly-impressions',
      date_format_labels: 'MMM, YYYY',
      filters: { goal_id: '' },
      dataSourceRequest: (payload: any) =>
        Company.activeGoalMonthly({
          id: this.filters.company_id,
          goal_id: payload?.filters?.goal_id || readGoalIdFromUrl() || this.filters.goal_id,
        }),
    }),
  ]
}
